import { error as logError } from "../../../utils/console";
import {
  buildFooterSummary,
  fetchCheatSheetBands,
  PRINT_COLUMN_GROUPS,
  type CheatSheetPrintParams,
  type PrintLegBand,
} from "./cheatSheetPrint";

/** ARGB fills matching the print sheet palette. */
const GROUP_FILLS: Record<string, string> = {
  "grp-basics": "FFD9D9D9",
  "grp-flight": "FFBDD7EE",
  "grp-wing": "FFF8CBAD",
  "grp-jib": "FFF6C1CF",
};
const TITLE_FILL = "FFD5E8C9";
const COLUMN_HEADER_FILL = "FFEFEFEF";
const UW_FILL = "FFDBE7F5";
const DW_FILL = "FFFBE5D6";
const ACCEL_COLOR = "FFC00000";
const BORDER_COLOR = "FF808080";

/** Excel worksheet names: max 31 chars, no \\ / * ? : [ ] characters. */
function sanitizeSheetName(name: string): string {
  const cleaned = name.replace(/[\\/*?:[\]]/g, " ").trim();
  return (cleaned === "" ? "Cheat Sheet" : cleaned).slice(0, 31);
}

function sanitizeFileName(name: string): string {
  const cleaned = name.replace(/[\\/:*?"<>|]/g, " ").replace(/\s+/g, " ").trim();
  return cleaned === "" ? "Cheat Sheet" : cleaned;
}

/** Keep numbers numeric so the sheet stays usable for calculations; pass "--" / "MAX" through. */
function numericOrText(text: string): number | string {
  const n = Number(text);
  return text.trim() !== "" && Number.isFinite(n) ? n : text;
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Which value the red acceleration column carries. */
export type CheatSheetAccelMode = "accel" | "delta";

/**
 * Export the config cheat sheet (straight-line values with red acceleration values,
 * UW/DW bands per TWS) as a styled .xlsx download.
 *
 * `mode` controls the red value: "accel" (default) prints the raw acceleration average,
 * "delta" prints the acceleration expressed as a delta from the straight-line value
 * (acceleration − straight-line).
 */
export async function exportCheatSheetExcel(
  params: CheatSheetPrintParams,
  options: { mode?: CheatSheetAccelMode } = {}
): Promise<{ success: boolean; message?: string }> {
  const { configName, logPrefix } = params;
  const mode = options.mode ?? "accel";
  const isDelta = mode === "delta";
  try {
    const [ExcelJS, data] = await Promise.all([import("exceljs"), fetchCheatSheetBands(params)]);
    if (data.message != null || data.bands == null) {
      return { success: false, message: data.message ?? "Failed to load cheat sheet data" };
    }
    if (data.bands.every((b) => b.rows.length === 0)) {
      return { success: false, message: "No data for the current filters" };
    }

    const columns = PRINT_COLUMN_GROUPS.flatMap((g) => g.columns);
    // Grid: col 1 = UW/DW band label, col 2 = TWS, then one column per metric — split into a
    // straight-line cell + a red acceleration cell (under one merged metric header) for the
    // metrics that carry an acceleration channel, a single cell for the ones that don't.
    const metricSpan = (col: (typeof columns)[number]) => (col.accelKey != null ? 2 : 1);
    let nextCol = 3;
    const layout = columns.map((col) => {
      const entry = { col, startCol: nextCol, span: metricSpan(col) };
      nextCol += entry.span;
      return entry;
    });
    const totalColumns = nextCol - 1;

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(sanitizeSheetName(configName), {
      views: [{ showGridLines: false }],
    });
    sheet.getColumn(1).width = 5;
    sheet.getColumn(2).width = 10;
    for (const { startCol, span } of layout) {
      // Split metrics get two narrower columns so the sheet stays roughly its previous width.
      const width = span === 2 ? 9 : 13;
      for (let c = startCol; c < startCol + span; c++) {
        sheet.getColumn(c).width = width;
      }
    }

    const thinBorder = {
      top: { style: "thin" as const, color: { argb: BORDER_COLOR } },
      left: { style: "thin" as const, color: { argb: BORDER_COLOR } },
      bottom: { style: "thin" as const, color: { argb: BORDER_COLOR } },
      right: { style: "thin" as const, color: { argb: BORDER_COLOR } },
    };
    const solidFill = (argb: string) => ({
      type: "pattern" as const,
      pattern: "solid" as const,
      fgColor: { argb },
    });
    const center = { horizontal: "center" as const, vertical: "middle" as const };

    // Row 1: title band.
    sheet.mergeCells(1, 1, 1, totalColumns);
    const titleCell = sheet.getCell(1, 1);
    titleCell.value = `${configName} - CHEAT SHEET${isDelta ? " (ACCEL DELTA)" : ""}`;
    titleCell.font = { bold: true, size: 16 };
    titleCell.fill = solidFill(TITLE_FILL);
    titleCell.alignment = center;
    titleCell.border = thinBorder;
    sheet.getRow(1).height = 26;

    // Row 2: colored group headers (BASICS also spans the TWS column). Styles go on before the
    // merge so every cell in the range carries them and the merged block keeps its full border.
    let groupStartCol = 2;
    for (const [i, group] of PRINT_COLUMN_GROUPS.entries()) {
      const span =
        group.columns.reduce((n, col) => n + metricSpan(col), 0) + (i === 0 ? 1 : 0);
      for (let c = groupStartCol; c < groupStartCol + span; c++) {
        const cell = sheet.getCell(2, c);
        cell.font = { bold: true, size: 13 };
        cell.fill = solidFill(GROUP_FILLS[group.cssClass] ?? COLUMN_HEADER_FILL);
        cell.alignment = center;
        cell.border = thinBorder;
      }
      sheet.getCell(2, groupStartCol).value = group.title;
      sheet.mergeCells(2, groupStartCol, 2, groupStartCol + span - 1);
      groupStartCol += span;
    }
    sheet.getCell(2, 1).border = thinBorder;
    sheet.getRow(2).height = 20;

    // Row 3: metric headers, each merged across its straight-line + acceleration cells.
    for (const c of [1, 2]) {
      const cell = sheet.getCell(3, c);
      cell.value = c === 1 ? "" : "TWS [KPH]";
      cell.font = { bold: true, size: 9 };
      cell.fill = solidFill(COLUMN_HEADER_FILL);
      cell.alignment = { ...center, wrapText: true };
      cell.border = thinBorder;
    }
    for (const { col, startCol, span } of layout) {
      for (let c = startCol; c < startCol + span; c++) {
        const cell = sheet.getCell(3, c);
        cell.font = { bold: true, size: 9 };
        cell.fill = solidFill(COLUMN_HEADER_FILL);
        cell.alignment = { ...center, wrapText: true };
        cell.border = thinBorder;
      }
      sheet.getCell(3, startCol).value = col.header;
      if (span > 1) {
        sheet.mergeCells(3, startCol, 3, startCol + span - 1);
      }
    }
    sheet.getRow(3).height = 24;

    // Data rows: UW band then DW band.
    let rowIndex = 4;
    for (const band of data.bands as PrintLegBand[]) {
      if (band.rows.length === 0) {
        const legName = band.label === "UW" ? "upwind" : "downwind";
        const bandCell = sheet.getCell(rowIndex, 1);
        bandCell.value = band.label;
        bandCell.font = { bold: true, size: 12 };
        bandCell.alignment = center;
        bandCell.border = thinBorder;
        sheet.mergeCells(rowIndex, 2, rowIndex, totalColumns);
        const cell = sheet.getCell(rowIndex, 2);
        cell.value = `No ${legName} data for the current filters`;
        cell.font = { italic: true, size: 11 };
        cell.border = thinBorder;
        rowIndex++;
        continue;
      }

      const bandStartRow = rowIndex;
      const rowFill = solidFill(band.label === "UW" ? UW_FILL : DW_FILL);
      for (const row of band.rows) {
        const twsCell = sheet.getCell(rowIndex, 2);
        const twsNum = Number(row.tws);
        twsCell.value = Number.isFinite(twsNum) ? twsNum : row.tws;
        twsCell.font = { bold: true, size: 12 };
        for (const [i, cellData] of row.cells.entries()) {
          const { startCol, span } = layout[i];
          const straightCell = sheet.getCell(rowIndex, startCol);
          straightCell.value = numericOrText(cellData.straight);
          straightCell.font = { size: 12 };
          if (span > 1) {
            // Own cell for the red value: blank when this band has no acceleration data.
            const accelText = isDelta ? cellData.accelDelta : cellData.accel;
            const accelCell = sheet.getCell(rowIndex, startCol + 1);
            accelCell.value = accelText == null ? null : numericOrText(accelText);
            accelCell.font = { size: 11, bold: true, color: { argb: ACCEL_COLOR } };
          }
        }
        for (let c = 2; c <= totalColumns; c++) {
          const cell = sheet.getCell(rowIndex, c);
          cell.fill = rowFill;
          cell.alignment = center;
          cell.border = thinBorder;
        }
        sheet.getRow(rowIndex).height = 18;
        rowIndex++;
      }
      sheet.mergeCells(bandStartRow, 1, rowIndex - 1, 1);
      const bandCell = sheet.getCell(bandStartRow, 1);
      bandCell.value = band.label;
      bandCell.font = { bold: true, size: 12 };
      bandCell.alignment = center;
      for (let r = bandStartRow; r < rowIndex; r++) {
        sheet.getCell(r, 1).border = thinBorder;
      }
    }

    // Footer: red accelerations note, any warnings, then the filter summary.
    rowIndex++;
    const noteCell = sheet.getCell(rowIndex, 1);
    sheet.mergeCells(rowIndex, 1, rowIndex, totalColumns);
    noteCell.value = isDelta
      ? "* Right-hand cell of each metric, in RED: the acceleration as a delta from the straight-line value (accel − straight-line)"
      : "* Right-hand cell of each metric, in RED: the acceleration value";
    noteCell.font = { bold: true, size: 10, color: { argb: ACCEL_COLOR } };
    rowIndex++;
    for (const warning of data.warnings) {
      sheet.mergeCells(rowIndex, 1, rowIndex, totalColumns);
      const cell = sheet.getCell(rowIndex, 1);
      cell.value = warning;
      cell.font = { size: 9, italic: true };
      rowIndex++;
    }
    sheet.mergeCells(rowIndex, 1, rowIndex, totalColumns);
    const summaryCell = sheet.getCell(rowIndex, 1);
    summaryCell.value = buildFooterSummary(configName, params.filterSummary);
    summaryCell.font = { size: 9 };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    downloadBlob(
      blob,
      `${sanitizeFileName(configName)} - Cheat Sheet${isDelta ? " (Delta)" : ""}.xlsx`
    );
    return { success: true };
  } catch (err) {
    logError(`${logPrefix}: export cheat sheet Excel failed`, err);
    return {
      success: false,
      message: err instanceof Error ? err.message : "Failed to export Excel file",
    };
  }
}
